/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import * as React from "react";
import classnames from "classnames";
import { FlexGrid, WithPropsGuard, LoadingBarIcon, ErrorContainer } from "../../../_common";
import styles from "./DocumentContainer.module.scss";
import { makeSafeReactInnerHTML } from "../../../../../utils/makeSafeReactInnerHTML";
import DocumentNavigator from "../../component/DocumentNavigator/DocumentNavigator";
import { LocalizedPolicyInformation, LocalizedPolicy, PolicyList } from "../../../../../api/legal/models/policies";
import MarkdownDocumentLogic from "../../../../../Logic/Legal/MarkdownDocumentLogic";
import { getLocalizedPolicyInfo, getLocalizedPolicyVersionPublishDateFromPolicies } from "../../utils";
import BasicLogic from "../../../../../Logic/Basic/basicLogic";
import { Subscribe } from "unstated";
import { AppState } from "../../../../../app-state/AppState";
import { getCurrentLanguage, t } from "../../../../../i18n/i18n";
import { InvalidLocalizedPolicyData } from "../../../../../api/legal/models/legal";

interface BaseProps {
  policies: PolicyList | null;
  localizedPolicy: LocalizedPolicy | LocalizedPolicyInformation;
  isLoading: boolean;
  className?: string;
}

interface Props extends BaseProps {
  basicLogic: BasicLogic;
}

interface State {
  error: Error | null;
  hash: string | null;
}

const Loading = () => (
  <FlexGrid flex={2} className={styles.loadingContainer}>
    <LoadingBarIcon />
  </FlexGrid>
);

const Error = ({ error }: { error: Error | null }) => (
  <ErrorContainer description={t("DocumentContainer.fileNotFound")} />
);

const DocumentContainer = WithPropsGuard(
  class DocumentContainer extends React.Component<Props, State> {
    logic: MarkdownDocumentLogic;

    constructor(props: Props) {
      super(props);
      const { localizedPolicy } = props;
      this.state = {
        error: null,
        hash: window.location.hash,
      };
      try {
        this.logic = new MarkdownDocumentLogic({ localizedPolicy });
        this.logic.subscribe(() => this.setState({}));
      } catch (error) {
        this.state = {
          error,
          hash: window.location.hash,
        };
      }
    }

    componentDidMount(): void {
      if (this.state.error) return;
      this.logic.init();
    }

    private renderNavigator() {
      const { error } = this.state;
      if (error) return null;

      const { headings, headerPrefix } = this.logic.state;
      if (!headings || !headings.length) return null;
      return <DocumentNavigator className={styles.documentNavigator} headerPrefix={headerPrefix} headings={headings} />;
    }

    private renderContent() {
      const { htmlString } = this.logic.state;
      const { hash } = this.state;
      const { localizedPolicy, policies } = this.props;
      if (!htmlString) return null;

      const details = getLocalizedPolicyInfo({ localizedPolicy, policies });
      const breadcrumbs: string[] = [];

      if (details.namespace) breadcrumbs.push(details.namespace);

      const date = getLocalizedPolicyVersionPublishDateFromPolicies({ localizedPolicy, policies });
      if (!!date) {
        const currentLanguage = getCurrentLanguage();
        const formattedDate = Intl.DateTimeFormat(currentLanguage, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(new Date(date));
        breadcrumbs.push(formattedDate);
      }

      if (!!hash) {
        const copyHash = hash;
        this.setState({ hash: null });
        setTimeout(() => {
          window.location.href = copyHash;
          const el = document.getElementById(copyHash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 10);
      }
      return (
        <FlexGrid flex={1} className={styles.content}>
          {breadcrumbs.length > 0 && (
            <div className={styles.breadcrumbs}>
              {breadcrumbs.map((breadcrumb) => (
                <div key={breadcrumb}>{breadcrumb}</div>
              ))}
            </div>
          )}
          <div className={styles.title}>{details.policyName}</div>
          <div className={styles.document} dangerouslySetInnerHTML={makeSafeReactInnerHTML(htmlString)} />
        </FlexGrid>
      );
    }

    render() {
      const { error } = this.state;
      if (error) return <Error error={error} />;

      const { className, localizedPolicy, isLoading } = this.props;
      const { isFetchingData, isGeneratingHTML, error: asLogicError } = this.logic.state;
      if (asLogicError) return <Error error={asLogicError} />;
      if (!localizedPolicy) return <Error error={new InvalidLocalizedPolicyData()} />;
      if (isFetchingData || isGeneratingHTML || isLoading) return <Loading />;
      return (
        <FlexGrid className={classnames(styles.documentContainer, className)}>
          {this.renderContent()}
          <div className={styles.navigator}>
            {this.renderNavigator()}
          </div>
        </FlexGrid>
      );
    }
  },
  (prevProps, nextProps) =>
    prevProps.localizedPolicy.id !== nextProps.localizedPolicy.id ||
    (prevProps.policies || {}).toString() !== (nextProps.policies || {}).toString()
);

export default (props: BaseProps) => (
  <Subscribe to={[AppState]}>
    {(appState: AppState) => {
      const { basicLogic } = appState.state;
      return <DocumentContainer basicLogic={basicLogic} {...props} />;
    }}
  </Subscribe>
);
